import { useAtom } from 'jotai/index';
import {
  creatingProjectLoadingAtom, deletingProjectLoadingAtom, downloadingProjectFileLoadingAtom,
  gettingProjectsLoadingAtom,
  projectsAtom, projectsCountAtom, projectsItemsOnPageAtom,
  projectsPagesInfoAtom, projectsSearchQueryAtom,
} from '../atoms/projectsAtoms';
import sendRequest from '../helpers/sendRequest';
import Api from '../api';
import {
  closeModal2Atom,
  closeModalAtom, modal2StateAtom, modalStateAtom, openConfirmModalAtom, openInfoModalAtom, openLoadingModalAtom,
} from '../atoms/modalAtoms';
import useTranslations from './useTranslations';
import NewProjectModal from '../pages/MyProjects/components/NewProjectModal';
import RenameProjectModal from '../pages/MyProjects/components/RenameProjectModal';
import { DICTIONARY } from '../translations/dictionary';
import FilesServices from '../services/FilesServices';
import { isAdminAtom } from '../atoms/accountAtoms';
import { configurationsProjectAtom } from '../atoms/configurationsAtoms';

// Shared by all hook instances: only the latest request may update the projects count.
let projectsCountRequestId = 0;
// Only the latest list request may update the list (fast resizes and searches send several).
let projectsListRequestId = 0;

const useProjects = ({ setSelectedItems } = {}) => {
  const { t } = useTranslations();

  const [isAdmin] = useAtom(isAdminAtom);
  const [projects, setProjects] = useAtom(projectsAtom);
  const [downloadingFileLoading, setDownloadingFileLoading] = useAtom(downloadingProjectFileLoadingAtom);
  const [gettingProjectsLoading, setGettingProjectsLoading] = useAtom(gettingProjectsLoadingAtom);
  const [creatingProjectLoading, setCreatingProjectLoading] = useAtom(creatingProjectLoadingAtom);
  const [deletingProjectLoading, setDeletingProjectLoading] = useAtom(deletingProjectLoadingAtom);
  const [projectsPagesInfo, setProjectsPagesInfo] = useAtom(projectsPagesInfoAtom);
  const [, setProjectsCount] = useAtom(projectsCountAtom);
  const [projectsItemsOnPage] = useAtom(projectsItemsOnPageAtom);
  const [, setConfigurationsProject] = useAtom(configurationsProjectAtom);

  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [, setModalState] = useAtom(modalStateAtom);
  const [, setModal2State] = useAtom(modal2StateAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [, closeModal2] = useAtom(closeModal2Atom);
  const [projectsSearchQuery, setProjectsSearchQuery] = useAtom(projectsSearchQueryAtom);

  const searchProjects = (query) => {
    const trimmed = query.trim();
    setProjectsSearchQuery(trimmed);
    setSelectedItems?.([]);
  };

  const refreshProjectsCount = async () => {
    projectsCountRequestId += 1;
    const requestId = projectsCountRequestId;

    const data = await sendRequest({
      request: Api.getProjects,
      payload: { page: 1, items_on_page: 1 },
      showErrorInModal: false,
      warnErrorText: 'while getting projects count',
    });

    if (data?.status !== 'ok' || requestId !== projectsCountRequestId) return;
    setProjectsCount(data.data.total_projects ?? null);
  };

  // itemsOnPage comes from the desktop grid size. Without it the server default is used.
  // Returns false when a newer request overtook this one, so its result was dropped.
  const getProjects = async ({
    page, shouldAddToExisting, search, itemsOnPage,
  }) => {
    const payload = { page, items_on_page: itemsOnPage || projectsItemsOnPage };
    if (search) payload.search = search;

    projectsListRequestId += 1;
    const listRequestId = projectsListRequestId;
    projectsCountRequestId += 1;
    const requestId = projectsCountRequestId;

    // Loading ends with the latest request only; an older one finishing first must not hide the loader.
    setGettingProjectsLoading(true);
    const data = await sendRequest({
      request: Api.getProjects,
      payload,
    });

    if (listRequestId !== projectsListRequestId) return false;
    setGettingProjectsLoading(false);
    if (data?.status !== 'ok') return true;

    if (page !== 1 && page !== '1' && shouldAddToExisting) setProjects((prev) => [...prev, ...data.data.projects]);
    else setProjects(data.data.projects);

    setProjectsPagesInfo(data.data.pages);
    // pages.total_records is filtered by search; total_projects is always the full count.
    // A response that was overtaken by a newer request (or by a create/delete) must not overwrite the count.
    if (requestId === projectsCountRequestId) setProjectsCount(data.data.total_projects ?? null);
    return true;
  };

  const createNewProject = (cb) => async (name) => {
    if (creatingProjectLoading) return;

    const data = await sendRequest({
      request: Api.createProject,
      payload: { name, configurations: [] },
      setLoading: setCreatingProjectLoading,
    });

    if (data?.data?.project) {
      setProjects((prev) => [data.data.project, ...prev]);
      setProjectsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records + 1 }));
      setProjectsCount((prev) => (prev == null ? prev : prev + 1));
      refreshProjectsCount().then();
      cb?.(data?.data?.project);
      closeModal2();
    }
  };

  const deleteProjects = async (listOfIds) => {
    if (deletingProjectLoading) return;

    const data = await sendRequest({
      request: Api.deleteProjects,
      payload: { ids: listOfIds },
      setLoading: setDeletingProjectLoading,
    });

    if (data?.status === 'ok') {
      setSelectedItems([]);
      setProjects((prev) => prev.filter((i) => !listOfIds.includes(i.id)));
      setProjectsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records - listOfIds.length }));
      setProjectsCount((prev) => (prev == null ? prev : Math.max(0, prev - listOfIds.length)));
      refreshProjectsCount().then();
      closeModal();
    }
  };

  /* Files Start */
  const downloadProjectsArchive = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();

    async function generateAndDownload(projectId) {
      const body = { project_id: projectId };

      const response = await sendRequest({
        request: Api.generateProjectsArchive,
        payload: body,
        setLoading: setDownloadingFileLoading,
        getFullResponse: true,
      });

      const { data, headers } = response || {};

      let isError = false;
      if (data?.status !== 'failed') {
        const downloadRes = FilesServices.downloadPdf(data, headers);
        isError = downloadRes.isError;
      }

      if (!isError) closeModal();
    }

    await Promise.all(ids.map((id) => generateAndDownload(id)));
    closeModal();
  };

  const downloadProjectsPdf = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();

    async function generateAndDownload(projectId) {
      const body = { project_id: projectId };

      const response = await sendRequest({
        request: Api.generateProjectsPdf,
        payload: body,
        setLoading: setDownloadingFileLoading,
        getFullResponse: true,
      });

      const { data, headers } = response || {};

      let isError = false;
      if (data?.status !== 'failed') {
        const downloadRes = FilesServices.downloadPdf(data, headers);
        isError = downloadRes.isError;
      }

      if (!isError) closeModal();
    }

    await Promise.all(ids.map((id) => generateAndDownload(id)));
    closeModal();
  };

  const downloadProjectsDocx = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();

    async function generateAndDownload(projectId) {
      const body = { project_id: projectId };

      const response = await sendRequest({
        request: Api.generateProjectsDocx,
        payload: body,
        setLoading: setDownloadingFileLoading,
        getFullResponse: true,
      });

      const { data, headers } = response || {};

      let isError = false;
      if (data?.status !== 'failed') {
        const downloadRes = FilesServices.downloadDocx(data, headers);
        isError = downloadRes.isError;
      }

      if (!isError) closeModal();
    }

    await Promise.all(ids.map((id) => generateAndDownload(id)));
    closeModal();
  };

  const downloadProjectsFile = async (ids) => {
    if (isAdmin) await downloadProjectsDocx(ids);
    else await downloadProjectsPdf(ids);
  };

  const emailProjectsArchive = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();
    let isError = false;

    async function generateAndSend(projectId) {
      const body = { project_id: projectId, return_to: 'mail' };

      const data = await sendRequest({
        request: Api.generateProjectsArchive,
        payload: body,
        setLoading: setDownloadingFileLoading,
      });

      if (data?.status === 'failed') {
        isError = true;
      }
    }

    await Promise.all(ids.map((id) => generateAndSend(id)));
    if (!isError) {
      openInfoModal({ text: t(DICTIONARY.emailSuccess) });
    } else {
      closeModal();
    }
  };
  /* Files End */

  const renameProject = async ({ id, name }) => {
    const data = await sendRequest({
      request: Api.updateProject,
      payload: { id, body: { name } },
      showErrorInModal: false,
      warnErrorText: 'while renaming project',
    });
    if (data?.status !== 'ok') return data;

    const nextName = data.data?.project?.name || data.data?.name || name;
    setProjects((prev) => prev.map((project) => (
      project.id === id ? { ...project, name: nextName } : project
    )));
    setConfigurationsProject((prev) => (
      prev?.id === id ? { ...prev, name: nextName } : prev
    ));

    return data;
  };

  const onRenameProjectClick = (project) => {
    setModalState({
      isOpen: true,
      withCloseButton: true,
      children: <RenameProjectModal project={project} onSave={renameProject} />,
    });
  };

  const onNewProjectClick = ({ cb } = {}) => {
    setModal2State({
      isOpen: true,
      children: <NewProjectModal onSave={createNewProject(cb)} />,
    });
  };

  const onDeleteProjectsClick = (listOfIds) => {
    openConfirmModal({
      text: listOfIds.length === 1 ? t(DICTIONARY.sureToDeleteProject) : t(DICTIONARY.sureToDelete, listOfIds.length),
      onButtonClick: () => deleteProjects(listOfIds),
    });
  };

  return {
    projects,
    gettingProjectsLoading,
    projectsPagesInfo,
    projectsSearchQuery,
    getProjects,
    refreshProjectsCount,
    searchProjects,
    onNewProjectClick,
    onRenameProjectClick,
    renameProject,
    onDeleteProjectsClick,
    downloadProjectsArchive,
    downloadProjectsFile,
    emailProjectsArchive,
  };
};

export default useProjects;
